/* eslint-disable import/no-cycle */
import { useContext, useState, useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { get, once } from 'lodash';

import { AppContext } from '$util/AppContext';
import { recordAnalytics } from '$util/analytics';
import { AnalyticsEvents } from '$util/analytics/types';
import { withResponsive } from '$util/theme';
import { PreloadImage } from '$util/PreloadImage';
import { carImagesSourceList } from '$util/image/carImages';
import type { PageMeta } from '$util/types';
import { getCarLogoImgSrc } from '$util/image';
import type { InjectComponentValue } from '$util/InjectComponent';
import { InjectComponentProvider } from '$util/InjectComponent';

import { nutHeaderSrc } from '$layout/partials/TopBackground';
import Grid from '$components/layout/Grid';
import { Image } from '$components/basic/Image';
import { VideoModal } from '$components/composite/VideoModal';

import type { VrmLookupInjectParams } from '$quantum/molecules/VrmLookup';
import VrmLookup, { VrmLookupInjectName } from '$quantum/molecules/VrmLookup';
import { VrmLookupCard } from '$quantum/organisms/VrmLookupCard';

import { PartOfRenault } from '$quantum/molecules/PartOfRenault';
import { Title } from '@/components/atoms/Title';
import { Icon } from '@/components/atoms/Icon';
import { PartnerText } from './components/PartnerText';
import { TopTitle } from './components/TopTitle';
import { Bottom } from './components/Bottom';
import { VideoHeaderImage } from './components/VideoHeaderImage';
import type { PartnerDataProps } from './types';
import MainWrapper, { WrapperWithVideo, SubTitle, Banner } from './styled';

const { Row, Col } = Grid;
const ResponsiveRow = withResponsive(Row);
const ResponsiveCol = withResponsive(Col);

const renderTopWrapper = (
  pageType: PageMeta[],
  pageTypeFromPath: string,
  pathname: string,
  subTitle: React.ReactNode,
  name: string,
  title: VrmLookupSectionProps['title'],
  image: VrmLookupSectionProps['image'],
  abTestCarServiceMake?: VrmLookupSectionProps['abTestCarServiceMake']
) => {
  const carMakeSlug = (pageType && pageType.find((pt) => pt.type === 'car')?.slug) || '';
  const isFree = get(pageType, '0.isFree', false);
  const carMakeLogoSrc = getCarLogoImgSrc(carMakeSlug);
  const carMakeImageSrc = get(carImagesSourceList, carMakeSlug, carImagesSourceList.default);

  switch (pageTypeFromPath) {
    case 'car': {
      return (
        <>
          <PreloadImage provider="cloudinary" src={carMakeLogoSrc} />

          <ResponsiveRow className="desktop top-wrapper car" displayFrom="md">
            <Col className="left-col" md={6}>
              <div className="top-title">
                <TopTitle pathname={pathname} pageTypeFromPath={pageTypeFromPath} />
              </div>
              <VrmLookupCard />
            </Col>
            <Col className="right-col" md={6}>
              <Image
                className="ImgCar"
                provider="cloudinary"
                src={carMakeImageSrc}
                alt={carMakeSlug}
                layout="fill"
                fill="natural"
              />
              <PartOfRenault />
            </Col>
          </ResponsiveRow>

          <ResponsiveRow className="mobile top-wrapper car" displayTo="md">
            <Col className="car-images" span={12}>
              <Image
                className="ImgCar"
                provider="cloudinary"
                src={carMakeImageSrc}
                alt={carMakeSlug}
                layout="fill"
                fill="natural"
              />
            </Col>
            <Col span={12}>
              <TopTitle pathname={pathname} pageTypeFromPath={pageTypeFromPath} />
              <VrmLookupCard />
              <div className="part-of-renault">
                <PartOfRenault />
              </div>
            </Col>
          </ResponsiveRow>
        </>
      );
    }
    case 'partner':
      return (
        <Row className="top-wrapper partner-top">
          <Col span={12}>
            <Title>Book hassle-free car maintenance</Title>
            <SubTitle>{subTitle}</SubTitle>
            <VrmLookupCard />
          </Col>
        </Row>
      );
    case 'symptom':
      <PreloadImage provider="cloudinary" src={image} width={86} />;

      return (
        <ResponsiveRow className="top-wrapper symptom-top">
          <Col md={12}>
            <div className="top-title ">
              {image && title && (
                <Image provider="cloudinary" src={image} alt={title} layout="fixed" width="86" height="80" />
              )}
              <TopTitle pathname={pathname} pageTypeFromPath={pageTypeFromPath} title={title} />
              <SubTitle>{subTitle}</SubTitle>
            </div>
          </Col>
        </ResponsiveRow>
      );
    case 'symptoms':
      return (
        <ResponsiveRow className="top-wrapper symptomsIndex-top">
          <Col md={12}>
            <TopTitle pathname={pathname} pageTypeFromPath={pageTypeFromPath} />
          </Col>
        </ResponsiveRow>
      );

    default:
      return (
        <Row className="top-wrapper main">
          {abTestCarServiceMake && (
            <ResponsiveCol className="car-images" span={12} displayTo="md">
              <Image
                className="ImgCar"
                provider="cloudinary"
                src={carMakeImageSrc}
                alt={carMakeSlug}
                layout="fixed"
                height={90}
                width={120}
              />
            </ResponsiveCol>
          )}
          <Col span={12}>
            <TopTitle pathname={pathname} pageTypeFromPath={pageTypeFromPath} />
            {subTitle && <SubTitle>{subTitle}</SubTitle>}
            <VrmLookupCard isFree={isFree} />
          </Col>
        </Row>
      );
  }
};

/**
 * @deprecated use src/components/molecules/TopNavBanner
 */
export function MotServiceBundleBanner() {
  return (
    <Banner className="mot-service">
      <Icon id="finance/tag-01-flipped" legacy={false} />
      <div>50% off your MOT with a Full Service</div>
    </Banner>
  );
}

export function PartnerBanner({
  pathname,
  partnerData,
}: {
  pathname: string;
  partnerData?: PartnerDataProps;
}) {
  return (
    <Banner className="partner-banner">
      <Image
        className="discount"
        provider="cloudinary"
        src="/icons/Icon_discount_quotes_ynqckm.svg"
        alt="discount"
        layout="fixed"
        width="25"
        height="26"
      />
      <PartnerText pathname={pathname} data={partnerData} />
    </Banner>
  );
}

const preloadVrmLookupImages = ({
  imagePostcode,
  imageRegistrationNumber,
}: VrmLookupInjectParams[VrmLookupInjectName.HEAD]): JSX.Element => (
  <>
    <PreloadImage provider="cloudinary" href={imagePostcode} />
    <PreloadImage provider="cloudinary" href={imageRegistrationNumber} />
  </>
);

const isReferralPage = (pathname: string) => pathname.startsWith('/me') || pathname.startsWith('/referral');

export interface VrmLookupSectionProps {
  readonly subTitle?: string;
  readonly partnerData?: PartnerDataProps;
  readonly title?: string;
  readonly image?: string;
  readonly abTestCarServiceMake?: boolean;
  readonly abTestMot?: boolean;
  readonly className?: string;
}

const sendAnalyticsHelpModalDisplayed = once(() =>
  recordAnalytics(AnalyticsEvents.VALUE_PROPOSITION_VIDEO_CLICKED)
);

/**
 * This component has 'preload' logic and it should be used only above the fold.
 *
 * This component hurts performance as it bundles logic for too many types of pages.
 * @todo refactor and don't use on pages that don't have VRMLookup form
 */
function VrmLookupSection({
  className,
  subTitle,
  title,
  image,
  partnerData,
  abTestCarServiceMake,
  abTestMot,
  ...props
}: VrmLookupSectionProps): JSX.Element {
  const {
    location: { pathname },
    pageType,
    pageTypeFromPath,
    meta,
  } = useContext(AppContext);
  const [width, setWidth] = useState(-1);

  const called = useRef(false);

  useEffect(() => {
    function getWidth() {
      if (process.browser) {
        setWidth(window.innerWidth);
      }
    }

    if (called.current === true) return;

    if (width === -1) {
      getWidth();
      called.current = true;
    }
  }, [width]);

  const showValueProposition = (path: string) => ['/', '/mot', '/car-service'].includes(path);

  const name = get(meta, 'name');

  const injectValueVrmLookup: InjectComponentValue = useMemo(() => {
    return {
      [VrmLookup.displayName]: {
        [VrmLookupInjectName.HEAD]: preloadVrmLookupImages,
      },
    };
  }, []);

  return (
    <InjectComponentProvider value={injectValueVrmLookup}>
      {pageTypeFromPath === 'partner' && <PartnerBanner pathname={pathname} partnerData={partnerData} />}

      {pathname.includes('mot-and-service') && <MotServiceBundleBanner />}

      {showValueProposition(pathname) || abTestMot ? (
        <WrapperWithVideo className={classNames('VrmLookupSection', className)}>
          {/** @TODO Contentful CMS driven images for the hero */}
          <VideoHeaderImage pathname={pathname} />
          <MainWrapper className={classNames('-withVideo')} {...props}>
            <VideoModal
              videoURL={
                pathname === '/mot'
                  ? 'https://player.vimeo.com/video/472976257'
                  : 'https://player.vimeo.com/video/472974622'
              }
              mute={width > 0 && width < 897}
              openCallback={sendAnalyticsHelpModalDisplayed}
            />
          </MainWrapper>
        </WrapperWithVideo>
      ) : (
        <MainWrapper className={classNames('VrmLookupSection', className)} {...props}>
          <PreloadImage provider="cloudinary" href={nutHeaderSrc} displayFrom="lg" />
          {renderTopWrapper(
            pageType,
            pageTypeFromPath,
            pathname,
            subTitle,
            name,
            title,
            image,
            abTestCarServiceMake
          )}
          {pageTypeFromPath && !isReferralPage(pathname) && (
            <Bottom pageType={pageType} pageTypeFromPath={pageTypeFromPath} />
          )}
        </MainWrapper>
      )}
    </InjectComponentProvider>
  );
}

VrmLookupSection.displayName = 'VrmLookupSection';
export default VrmLookupSection;
